import React, { useMemo, useState } from 'react';
import { useLocale } from '../context/LocaleContext';
import { Link, useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Button from '../components/Button';
import ChooseSourceStep from '../components/expenses/import/ChooseSourceStep';
import PasteStep from '../components/expenses/import/PasteStep';
import MatchStep from '../components/expenses/import/MatchStep';
import PreviewStep from '../components/expenses/import/PreviewStep';
import { useExpenses } from '../data/useExpenses';
import {
  parseDelimited, autoMatch, buildImportRows, importSummary, SAMPLE_CSV,
  importHeaders, importFileRefusal, importDoneText, NOTHING_TO_IMPORT,
} from '../domain/importDelimited';
import s from '../components/expenses/import.module.css';

// Import Expenses & Other Revenue — the four-step wizard MMS opens from the
// Actions menu: choose a source, paste or upload with defaults, confirm the
// column matching, review, import.
//
// Assembly and step state only; each step renders itself, and the parsing is in
// domain/importDelimited where it is tested without a browser.

export default function ExpenseImportPage() {
  const navigate = useNavigate();
  const { categoriesFor, importEntries } = useExpenses();
  // The importer parses amounts into minor units, which is a currency
  // question — see parseAmountMinor — and reads a file's dates in the
  // studio's order and a lone decimal mark by its Region (#532).
  const { currency, datePattern, decimalSeparator } = useLocale();

  const [step, setStep] = useState(1);
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [hasHeader, setHasHeader] = useState(true);
  const [defaultExpense, setDefaultExpense] = useState('');
  const [defaultRevenue, setDefaultRevenue] = useState('');
  const [mapping, setMapping] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);

  const rows = useMemo(() => (text.trim() ? parseDelimited(text) : []), [text]);
  const headers = importHeaders(rows, hasHeader);

  // The studio's own categories, which a row's Category cell is checked
  // against rather than written straight through (#134). Memoised on
  // `categoriesFor` — itself memoised by the provider — so the build below
  // does not recompute on every keystroke in the textarea.
  const expenseCategories = useMemo(() => categoriesFor('expense'), [categoriesFor]);
  const revenueCategories = useMemo(() => categoriesFor('revenue'), [categoriesFor]);

  const built = useMemo(() => (
    rows.length && mapping.length
      ? buildImportRows({
          rows,
          mapping,
          hasHeader,
          currency,
          datePattern,
          decimalSeparator,
          defaultExpenseCategory: defaultExpense,
          defaultRevenueCategory: defaultRevenue,
          expenseCategories,
          revenueCategories,
        })
      : []
  ), [rows, mapping, hasHeader, currency, datePattern, decimalSeparator, defaultExpense, defaultRevenue,
      expenseCategories, revenueCategories]);

  const summary = useMemo(() => importSummary(built), [built]);
  const importable = built.filter((r) => r.problems.length === 0);
  const pageCount = Math.max(1, Math.ceil(built.length / perPage));
  const shown = built.slice((page - 1) * perPage, page * perPage);

  const cancel = () => navigate('/expenses');

  const onFile = async (file) => {
    if (!file) return;
    // .xlsx is a zip archive, not text — reading it as text yields binary noise
    // that would parse into nonsense rows. Say so rather than importing garbage.
    const refusal = importFileRefusal(file.name);
    if (refusal) {
      setError(refusal);
      return;
    }
    setError(null);
    setFileName(file.name);
    setText(await file.text());
    setStep(2);
  };

  const downloadSample = () => {
    const url = URL.createObjectURL(new Blob([SAMPLE_CSV], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'coda-expenses-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const toMatchStep = () => {
    const parsed = text.trim() ? parseDelimited(text) : [];
    if (!parsed.length) { setError(NOTHING_TO_IMPORT); return; }
    setError(null);
    setMapping(autoMatch(hasHeader ? parsed[0] : parsed[0].map(() => '')));
    setStep(3);
  };

  const runImport = async () => {
    setBusy(true);
    setError(null);
    try {
      setDone(await importEntries(importable));
    } catch (err) {
      setError(err?.message || 'The import could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Expenses & Other Revenue">
      <div className={s.im}>
        <Link to="/expenses" className={s.imBack}>‹ Back to Expenses &amp; Other Revenue</Link>
        <div className={s.imH1}>Import Expenses &amp; Other Revenue</div>

        {error ? <div className={s.imErr}>{error}</div> : null}

        {done != null ? (
          <div className={s.imCard}>
            <div className={s.imQ}>{importDoneText(done, summary).title}</div>
            <div className={s.imHint}>{importDoneText(done, summary).detail}</div>
            <div className={s.imFoot}>
              <span className={s.spacer} />
              <Button onClick={cancel}>Done</Button>
            </div>
          </div>
        ) : null}

        {done == null && step === 1 ? (
          <ChooseSourceStep
            onFile={onFile}
            onPaste={() => { setFileName(''); setStep(2); }}
            onSample={downloadSample}
          />
        ) : null}

        {done == null && step === 2 ? (
          <PasteStep
            text={text}
            onTextChange={setText}
            fileName={fileName}
            hasHeader={hasHeader}
            onHasHeaderChange={setHasHeader}
            expenseCategories={expenseCategories}
            revenueCategories={revenueCategories}
            defaultExpense={defaultExpense}
            defaultRevenue={defaultRevenue}
            onDefaultExpenseChange={setDefaultExpense}
            onDefaultRevenueChange={setDefaultRevenue}
            onBack={() => setStep(1)}
            onCancel={cancel}
            onNext={toMatchStep}
          />
        ) : null}

        {done == null && step === 3 ? (
          <MatchStep
            headers={headers}
            rows={rows}
            mapping={mapping}
            hasHeader={hasHeader}
            onMappingChange={setMapping}
            onBack={() => setStep(2)}
            onCancel={cancel}
            onNext={() => { setPage(1); setStep(4); }}
          />
        ) : null}

        {done == null && step === 4 ? (
          <PreviewStep
            rows={shown}
            summary={summary}
            page={page}
            pageCount={pageCount}
            perPage={perPage}
            total={built.length}
            onPageChange={setPage}
            onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
            onBack={() => setStep(3)}
            onCancel={cancel}
            onImport={runImport}
            busy={busy}
          />
        ) : null}
      </div>
    </AppShell>
  );
}
